import React from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { formatDateTimeInViewerTZ } from '../utils/dateTimeUtils';

const CURRENT_MEDIA = {
  yes: 'I consent the use of my child\'s session media—including screenshots, video clips, and testimonials—strictly for non-commercial educational and informational uses.',
  no: 'I do not consent the use of my child\'s session media—including screenshots, video clips, and testimonials—strictly for non-commercial educational and informational uses.',
};

const MEDIA_BY_VERSION = {
  '2026-10-06': CURRENT_MEDIA,
  '2026-09-24': {
    yes: 'I give permission for Bacchapanti to use my child’s photographs, screenshots, videos, or testimonials for educational, informational, or promotional purposes.',
    no: 'I do not give permission for Bacchapanti to use my child’s photographs, screenshots, videos, or testimonials.',
  },
  '2026-09-23': {
    yes: 'Yes, you may use them for education and promotion',
    no: 'No, please do not use them',
  },
};

const RECORDING_BY_VERSION = {
  '2026-09-24': {
    yes: 'I consent to my child’s participation in recorded sessions.',
    no: 'I do not give permission to record sessions.',
  },
  '2026-09-23': {
    yes: 'Yes, you may record sessions for quality and training',
    no: 'No, please do not record my child',
  },
};

function choiceSentence(map, version, fallbackVersion, consented) {
  const text = map[version] || map[fallbackVersion];
  return text[consented ? 'yes' : 'no'];
}

function ConsentChoice({ label, consented, sentence }) {
  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, mb: 0.75 }}>
        <Chip
          size="small"
          label={consented ? 'Consented' : 'Did not consent'}
          color={consented ? 'success' : 'default'}
          variant={consented ? 'filled' : 'outlined'}
        />
      </Box>
      <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
        {sentence}
      </Typography>
    </Grid>
  );
}

function Detail({ label, value }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value || 'Not provided'}
      </Typography>
    </Grid>
  );
}

export default function ParentConsentSummary({ consent }) {
  const hasRecording = consent
    && (consent.session_recording_consent === true || consent.session_recording_consent === false);
  const signedAt = consent?.signed_at
    ? formatDateTimeInViewerTZ(consent.signed_at)
    : '';

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
        Parent Consent
      </Typography>
      {!consent ? (
        <Typography variant="body1" color="text.secondary">
          No parent consent on file
        </Typography>
      ) : (
        <Grid container spacing={2}>
          <ConsentChoice
            label="Screenshots & videos"
            consented={consent.media_use_consent === true}
            sentence={choiceSentence(
              MEDIA_BY_VERSION,
              consent.consent_version,
              '2026-10-06',
              consent.media_use_consent === true,
            )}
          />
          {hasRecording ? (
            <ConsentChoice
              label="Session recording"
              consented={consent.session_recording_consent === true}
              sentence={choiceSentence(
                RECORDING_BY_VERSION,
                consent.consent_version,
                '2026-09-24',
                consent.session_recording_consent === true,
              )}
            />
          ) : null}
          <Detail label="Signed by" value={consent.signer_name} />
          <Detail label="Electronic signature" value={consent.electronic_signature} />
          <Detail label="Date" value={signedAt} />
          <Detail label="Consent version" value={consent.consent_version} />
        </Grid>
      )}
    </Box>
  );
}
